import { ref, computed, watch, onBeforeUnmount, type Ref, type ComputedRef } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { useApi } from '@shopexperts/shared';
import { useAuthStore } from '@/store/auth';

/**
 * Composable for searching users with debounced search
 * Searches only the authenticated expert’s past clients
 */
export function useUserSearch(searchQuery: Ref<string> | ComputedRef<string>) {
  const api = useApi();
  const auth = useAuthStore();
  const debouncedQuery = ref('');
  let debounceTimeout: ReturnType<typeof setTimeout> | null = null;

  watch(searchQuery, (newValue) => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    debounceTimeout = setTimeout(() => {
      debouncedQuery.value = newValue;
    }, 300);
  });

  onBeforeUnmount(() => { if (debounceTimeout) clearTimeout(debounceTimeout); });

  const shouldSearch = computed(() => !!auth.user?.id && debouncedQuery.value.trim().length >= 2);

  const { data: results, isFetching, error } = useQuery({
    queryKey: computed(() => ['expert', 'client-search', auth.user?.id, debouncedQuery.value]),
    queryFn: async () => {

        const response = await api.expert.reviewsV2.searchUsers({
            query: {
                query: debouncedQuery.value
            }
        });
        return response;
    },

    enabled: shouldSearch,
    staleTime: 30000, // 30 seconds
    gcTime: 60000, // 1 minute
  });

  const data = computed(() => shouldSearch.value && searchQuery.value === debouncedQuery.value ? results.value : undefined);
  const isSearching = computed(() => isFetching.value || searchQuery.value !== debouncedQuery.value);
  const searchError = computed(() => error.value);

  const hasResults = computed(() => {
    const results = data.value;
    return results && results.length > 0;
  });

  return {
    data,
    isSearching,
    searchError,
    hasResults,
  };
}

